import { useEffect, useState, useContext, useCallback, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { ListGroup, ListGroupItem, Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap";
import { Phone, ReceiptText, ScanQrCode } from "lucide-react";
import UserContext from "../auth/UserContext";
import ValetApi from "../api/Api";
import DamageImage from "./DamageImage";
import DamageReportReview from "./DamageReportReview";
import { safeJsonParse } from "../utility/safeJsonParse";
import { formatPhone } from "../utility/formatPhone";
import { compressImageFile } from "../utility/compressImageFile";
import { VEHICLE_COLOR_OPTIONS, VEHICLE_MAKE_OPTIONS } from "../constants/vehicleMetaOptions";
import { toast } from "react-toastify";
import "./GarageDetail.css";
import LiveTicketScannerModal from "../components/LiveTicketScannerModalNew";
import PaymentCollectionModal from "./PaymentCollectionModal";
import { useAppSettings } from "../settings/AppSettingsContext";

const MAX_RAW_DAMAGE_PHOTO_BYTES = 8 * 1024 * 1024;
const MAX_COMPRESSED_DAMAGE_PHOTO_BYTES = 2 * 1024 * 1024;

function isMobileCaptureDevice() {
    if (typeof navigator === "undefined" || typeof window === "undefined") return false;

    const ua = String(navigator.userAgent || "");
    const uaDataMobile = typeof navigator.userAgentData?.mobile === "boolean" ? navigator.userAgentData.mobile : false;
    const maxTouchPoints = Number(navigator.maxTouchPoints || 0);
    const coarsePointer = typeof window.matchMedia === "function" ? window.matchMedia("(pointer: coarse)").matches : false;
    const matchesMobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

    return Boolean(uaDataMobile || matchesMobileUa || (maxTouchPoints > 1 && coarsePointer));
}

const GarageDetail = () => {
    const { vehicleId } = useParams();
    const { userDetail, activePodiumId, stageVehicle, parkVehicle, requestVehicle, retrieveVehicle, checkoutVehicle } = useContext(UserContext);
    const [transactionDetail, setTransactionDetail] = useState(null);
    const [transactionHistory, setTransactionHistory] = useState(null);
    const [showDamages, setShowDamages] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [damageAssessment, setDamageAssessment] = useState(null);
    const [isDamageAssessmentLoading, setIsDamageAssessmentLoading] = useState(false);
    const [damageAssessmentError, setDamageAssessmentError] = useState("");
    const replaceDamagePhotoInputRef = useRef(null);
    const [replaceTargetPhotoId, setReplaceTargetPhotoId] = useState(null);
    const [isDamagePhotoMutating, setIsDamagePhotoMutating] = useState(false);
    const [activeDamagePhotoActionId, setActiveDamagePhotoActionId] = useState(null);
    const [activeDamagePhotoActionType, setActiveDamagePhotoActionType] = useState("");
    const [isDamageReportLoading, setIsDamageReportLoading] = useState(false);
    const [showDamageReportModal, setShowDamageReportModal] = useState(false);
    const [damageReport, setDamageReport] = useState(null);
    const [damageReportError, setDamageReportError] = useState("");

    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const [typedVerificationInput, setTypedVerificationInput] = useState("");
    const [scannedTicket, setScannedTicket] = useState("");
    const [verifyStatus, setVerifyStatus] = useState("");
    const [verifyMessage, setVerifyMessage] = useState("");
    const [isCheckingOut, setIsCheckingOut] = useState(false);

    const [isActionBusy, setIsActionBusy] = useState(false);
    const [parkingSpotInput, setParkingSpotInput] = useState("");
    const [isEditingParkingSpot, setIsEditingParkingSpot] = useState(false);
    const [isSavingParkingSpot, setIsSavingParkingSpot] = useState(false);
    const [vehicleMetaDraft, setVehicleMetaDraft] = useState({ make: "", color: "", notes: "" });
    const [isEditingVehicleMeta, setIsEditingVehicleMeta] = useState(false);
    const [isSavingVehicleMeta, setIsSavingVehicleMeta] = useState(false);
    const [showParkingSpotZoom, setShowParkingSpotZoom] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [destinationPodiums, setDestinationPodiums] = useState([]);
    const [transferDestinationPodiumId, setTransferDestinationPodiumId] = useState("");
    const [transferNote, setTransferNote] = useState("");
    const [isTransferBusy, setIsTransferBusy] = useState(false);
    const [showLostKeysModal, setShowLostKeysModal] = useState(false);
    const [isLostKeysLoading, setIsLostKeysLoading] = useState(false);
    const [lostKeysData, setLostKeysData] = useState({ anchorUsername: "", parked: [], retrieved: [] });
    const [isPaymentsFeatureEnabled, setIsPaymentsFeatureEnabled] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [isPaymentStatusLoading, setIsPaymentStatusLoading] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const { settings } = useAppSettings();
    const canCapturePhotoOnDevice = isMobileCaptureDevice();

    const history = useHistory();
    const activePodiumRecord = (userDetail?.accessiblePodiums || []).find((podium) => Number(podium.id) === Number(activePodiumId)) || null;
    const activeTransferSourcePodiumId = Number(activePodiumId || 0) || null;
    const activeTransferSourceLocationId = Number(activePodiumRecord?.locationId || userDetail?.locationId || transactionDetail?.locationId || 0) || null;
    const activeTransferSourcePodiumName = activePodiumRecord?.name || transactionDetail?.podiumName || "Active Podium";

    const statusLabelById = {
        1: "Checked In",
        2: "Staged",
        3: "Parked",
        4: "Requested",
        5: "Retrieved",
        6: "Checked Out",
    };

    function applyLocalStatus(nextStatusId) {
        const nextStatusLabel = statusLabelById[nextStatusId] || "Unknown";
        setTransactionDetail((prev) => ({
            ...prev,
            transactionStatusId: nextStatusId,
            vehicleStatusId: nextStatusId,
            transactionStatusDescription: nextStatusLabel,
            vehicleStatusDescription: nextStatusLabel,
            statusDescription: nextStatusLabel,
        }));
    }

    function formatHistoryTimestampShort(timestamp) {
        const date = new Date(timestamp);
        if (Number.isNaN(date.getTime())) return "";

        const month = date.getMonth() + 1;
        const day = date.getDate();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const rawHour = date.getHours();
        const hour12 = rawHour % 12 || 12;
        const meridiem = rawHour >= 12 ? "P" : "A";

        return `${month}/${day} ${hour12}:${minutes}${meridiem}`;
    }

    function formatHistoryUser(row) {
        const first = String(row?.userFirstName || "").trim();
        const last = String(row?.userLastName || "").trim();

        if (first && last) {
            return `${first.charAt(0).toUpperCase()}. ${last}`;
        }

        return row?.username || "";
    }

    function getVehicleMetaDraftFromDetail(detail) {
        return {
            make: String(detail?.vehicleMake || "").trim(),
            color: String(detail?.vehicleColor || "")
                .trim()
                .toLowerCase(),
            notes: String(detail?.vehicleNotes || ""),
        };
    }

    function handleStartEditVehicleMeta() {
        setVehicleMetaDraft(getVehicleMetaDraftFromDetail(transactionDetail));
        setIsEditingVehicleMeta(true);
    }

    function handleCancelEditVehicleMeta() {
        setVehicleMetaDraft(getVehicleMetaDraftFromDetail(transactionDetail));
        setIsEditingVehicleMeta(false);
    }

    async function handleSaveVehicleMeta() {
        if (!transactionDetail?.vehicleId || isSavingVehicleMeta) return;

        const normalizedMake = String(vehicleMetaDraft?.make || "").trim();
        const normalizedColor = String(vehicleMetaDraft?.color || "")
            .trim()
            .toLowerCase();
        const normalizedNotes = String(vehicleMetaDraft?.notes || "").trim();

        if (!normalizedMake) {
            toast.error("Vehicle make is required.", { autoClose: 1000 });
            return;
        }

        if (!normalizedColor) {
            toast.error("Vehicle color is required.", { autoClose: 1000 });
            return;
        }

        try {
            setIsSavingVehicleMeta(true);
            await ValetApi.updateVehicle(transactionDetail.vehicleId, {
                make: normalizedMake,
                color: normalizedColor,
                notes: normalizedNotes,
            });
            await fetchTransactions();
            setIsEditingVehicleMeta(false);
            toast.success("Vehicle details saved", { autoClose: 900 });
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Unable to save vehicle details"), { autoClose: 1000 });
        } finally {
            setIsSavingVehicleMeta(false);
        }
    }

    useEffect(() => {
        let cancelled = false;

        async function fetchPaymentsFeatureStatus() {
            try {
                const res = await ValetApi.getPaymentsFeatureStatus();
                if (!cancelled) {
                    setIsPaymentsFeatureEnabled(Boolean(res?.data?.featureEnabled === true));
                }
            } catch {
                if (!cancelled) {
                    setIsPaymentsFeatureEnabled(false);
                }
            }
        }

        fetchPaymentsFeatureStatus();

        return () => {
            cancelled = true;
        };
    }, []);

    const fetchPaymentStatus = useCallback(
        async (vehicleIdArg) => {
            if (!vehicleIdArg) return;

            if (isPaymentsFeatureEnabled !== true) {
                setPaymentStatus(null);
                setIsPaymentStatusLoading(false);
                return;
            }

            try {
                setIsPaymentStatusLoading(true);
                const res = await ValetApi.getVehiclePaymentStatus(vehicleIdArg);
                setPaymentStatus(res?.data?.status || null);
            } catch (err) {
                setPaymentStatus(null);
                console.error(err);
            } finally {
                setIsPaymentStatusLoading(false);
            }
        },
        [isPaymentsFeatureEnabled],
    );

    const fetchTransactions = useCallback(async () => {
        // async function fetchTransactions() {
        try {
            // Fetch latest data from API
            const res = await ValetApi.getLatestTransactionDetailByVehicleId(vehicleId, activePodiumId);
            const nextTransactionDetail = res?.data?.transactionDetail?.[0] || null;
            setTransactionDetail(nextTransactionDetail);
            setParkingSpotInput(nextTransactionDetail?.parkingSpot || "");
            setVehicleMetaDraft(getVehicleMetaDraftFromDetail(nextTransactionDetail));
            setIsEditingParkingSpot(false);
            setIsEditingVehicleMeta(false);
            const historyRes = await ValetApi.getTransactionHistoryByVehicleId(vehicleId);
            setTransactionHistory(Array.isArray(historyRes.data.transactionHistory) ? historyRes.data.transactionHistory : []);
        } catch (err) {
            toast.error(err?.response?.data?.error || err?.message || "Error fetching transaction details", { autoClose: 1000 });
            console.error(err);
        }
    }, [vehicleId, activePodiumId]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    useEffect(() => {
        let cancelled = false;

        async function fetchDamageAssessment() {
            if (!showDamages || !transactionDetail?.transactionId) return;

            try {
                setIsDamageAssessmentLoading(true);
                setDamageAssessmentError("");

                const res = await ValetApi.getTransactionDamages(transactionDetail.transactionId);
                if (!cancelled) {
                    setDamageAssessment(res?.data?.damages || null);
                }
            } catch (err) {
                if (!cancelled) {
                    setDamageAssessment(null);
                    setDamageAssessmentError(err?.[0] || err?.response?.data?.error?.message || "Unable to load damage evidence");
                }
            } finally {
                if (!cancelled) {
                    setIsDamageAssessmentLoading(false);
                }
            }
        }

        fetchDamageAssessment();

        return () => {
            cancelled = true;
        };
    }, [showDamages, transactionDetail?.transactionId]);

    useEffect(() => {
        if (!transactionDetail?.vehicleId) return;

        if (isPaymentsFeatureEnabled !== true) {
            setPaymentStatus(null);
            setIsPaymentStatusLoading(false);
            return;
        }

        fetchPaymentStatus(transactionDetail.vehicleId);
    }, [transactionDetail?.vehicleId, transactionDetail?.podiumId, activePodiumId, isPaymentsFeatureEnabled, fetchPaymentStatus]);

    useEffect(() => {
        async function fetchDestinationPodiums() {
            try {
                if (!showTransferModal || !activeTransferSourceLocationId) {
                    setDestinationPodiums([]);
                    return;
                }

                const res = await ValetApi.getPodiumsByLocation(activeTransferSourceLocationId);
                const allPodiums = Array.isArray(res?.data?.podiums) ? res.data.podiums : [];
                const options = allPodiums.filter((p) => Number(p.id) !== Number(activeTransferSourcePodiumId));
                setDestinationPodiums(options);
            } catch (err) {
                setDestinationPodiums([]);
                toast.error(err?.[0] || err?.response?.data?.error?.message || "Error loading destination podiums", { autoClose: 1000 });
            }
        }
        fetchDestinationPodiums();
    }, [showTransferModal, activeTransferSourceLocationId, activeTransferSourcePodiumId]);

    function normalizeDigits(value) {
        return String(value || "").replace(/\D/g, "");
    }

    function getApiErrorMessage(err, fallback = "Something went wrong") {
        return err?.[0] || err?.response?.data?.error?.message || err?.response?.data?.error || err?.message || fallback;
    }

    function getLast4(value) {
        const digits = normalizeDigits(value);
        return digits.slice(-4);
    }

    function getMaskedPhone(value) {
        const digits = normalizeDigits(value);
        if (!digits) return "";
        if (digits.length <= 4) return digits;
        return `XXXXXXX${digits.slice(-4)}`;
    }

    function normalizeLostKeysResponse(payload) {
        if (payload && typeof payload === "object" && !Array.isArray(payload)) {
            return {
                anchorUsername: String(payload.anchorUsername || "").trim(),
                parked: Array.isArray(payload.parked) ? payload.parked : [],
                retrieved: Array.isArray(payload.retrieved) ? payload.retrieved : [],
            };
        }

        if (Array.isArray(payload)) {
            return {
                anchorUsername: "",
                parked: payload.filter((row) => Number(row?.transactionStatusId) === 3).slice(0, 3),
                retrieved: payload.filter((row) => Number(row?.transactionStatusId) === 5).slice(0, 3),
            };
        }

        return { anchorUsername: "", parked: [], retrieved: [] };
    }

    function resetVerifyState() {
        setTypedVerificationInput("");
        setScannedTicket("");
        setVerifyStatus("");
        setVerifyMessage("");
    }

    async function handleVerifyAndCheckout(verifyType = "ticket", scannedValue = "") {
        if (!transactionDetail || isCheckingOut) return;

        // Protect against React click event object being passed in from onClick
        const safeScannedValue = typeof scannedValue === "string" || typeof scannedValue === "number" ? String(scannedValue) : "";

        const entered = normalizeDigits(safeScannedValue || typedVerificationInput || scannedTicket);

        if (verifyType === "ticket") {
            const expectedTicket = normalizeDigits(transactionDetail.vehicleTicketNum);

            if (!entered) {
                setVerifyStatus("fail");
                setVerifyMessage("FAIL: Enter or scan a ticket number");
                toast.error("FAIL: Enter or scan a ticket number");
                return;
            }

            if (entered !== expectedTicket) {
                setVerifyStatus("fail");
                setVerifyMessage("FAIL: Ticket number does not match");
                toast.error("FAIL: Ticket number does not match");
                return;
            }

            setVerifyStatus("");
            setVerifyMessage(`Ticket ${entered} verified. Processing checkout...`);
        } else {
            const expectedLast4 = getLast4(transactionDetail.vehicleMobile);
            const enteredLast4 = entered.slice(-4);

            if (!enteredLast4 || enteredLast4.length < 4) {
                setVerifyStatus("fail");
                setVerifyMessage("FAIL: Enter at least 4 phone digits");
                toast.error("FAIL: Enter at least 4 phone digits");
                return;
            }

            if (enteredLast4 !== expectedLast4) {
                setVerifyStatus("fail");
                setVerifyMessage("FAIL: Last 4 digits do not match");
                toast.error("FAIL: Last 4 digits do not match");
                return;
            }

            setVerifyStatus("");
            setVerifyMessage(`Phone last 4 (${enteredLast4}) verified. Processing checkout...`);
        }

        try {
            setIsCheckingOut(true);

            const result = await checkoutVehicle(userDetail.id, transactionDetail.vehicleId, transactionDetail.podiumId, transactionDetail.locationId);

            if (result?.success) {
                setShowVerifyModal(false);
                resetVerifyState();
            } else {
                setVerifyStatus("fail");
                setVerifyMessage("FAIL: Ticket matched, but checkout did not complete.");
                toast.error("FAIL: Checkout did not complete. Please try again.");
            }
        } finally {
            setIsCheckingOut(false);
            await fetchTransactions();
        }
    }

    async function handleInitiateTransfer() {
        if (!transactionDetail || !transferDestinationPodiumId || isTransferBusy) return;

        const sourcePodiumId = Number(activeTransferSourcePodiumId || 0);
        const destinationPodiumId = Number(transferDestinationPodiumId || 0);

        if (!sourcePodiumId) {
            toast.info("Please select an active podium before transferring.", { autoClose: 1200 });
            return;
        }

        if (sourcePodiumId === destinationPodiumId) {
            toast.info("Transfer destination must be different from your active podium.", { autoClose: 1200 });
            return;
        }

        try {
            setIsTransferBusy(true);

            // Re-check latest vehicle podium before initiating to avoid stale transfer source errors.
            const latestDetailRes = await ValetApi.getLatestTransactionDetailByVehicleId(Number(transactionDetail.vehicleId));
            const latestDetail = latestDetailRes?.data?.transactionDetail?.[0] || null;
            const latestPodiumId = Number(latestDetail?.podiumId || 0) || null;

            if (latestPodiumId && latestPodiumId !== sourcePodiumId) {
                const latestPodiumName = String(latestDetail?.podiumName || "another podium").trim();
                toast.info(`Vehicle is currently at ${latestPodiumName}. Switch Active Podium, then try transfer again.`, { autoClose: 1600 });
                await fetchTransactions();
                return;
            }

            await ValetApi.initiateTransfer(Number(transactionDetail.vehicleId), sourcePodiumId, destinationPodiumId, transferNote || "");
            toast.success("Transfer initiated. Awaiting destination acceptance.", { autoClose: 1200 });
            setShowTransferModal(false);
            setTransferDestinationPodiumId("");
            setTransferNote("");
            await fetchTransactions();
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to initiate transfer", { autoClose: 1200 });
        } finally {
            setIsTransferBusy(false);
        }
    }

    async function handleSaveParkingSpot() {
        if (!transactionDetail?.transactionId || isSavingParkingSpot) return;

        try {
            setIsSavingParkingSpot(true);
            const normalizedSpot = (parkingSpotInput || "").trim();
            await ValetApi.updateTransactionById(transactionDetail.transactionId, { parkingSpot: normalizedSpot || null });
            await fetchTransactions();
            setIsEditingParkingSpot(false);
            toast.success("Parking spot saved", { autoClose: 900 });
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || "Unable to save parking spot", { autoClose: 1000 });
        } finally {
            setIsSavingParkingSpot(false);
        }
    }

    async function handleOpenLostKeys() {
        if (!transactionDetail?.transactionId) return;

        setShowLostKeysModal(true);
        setIsLostKeysLoading(true);
        setLostKeysData({ anchorUsername: "", parked: [], retrieved: [] });

        try {
            const res = await ValetApi.lostKeysNew(transactionDetail.transactionUserId, transactionDetail.transactionId);
            const normalized = normalizeLostKeysResponse(res?.data?.garageData);
            setLostKeysData(normalized);
        } catch (err) {
            setLostKeysData({ anchorUsername: "", parked: [], retrieved: [] });
            const message = getApiErrorMessage(err, "Unable to load lost keys activity");
            toast.error(message, { autoClose: 1200 });
        } finally {
            setIsLostKeysLoading(false);
        }
    }

    function handleStartReplaceDamagePhoto(photoId) {
        if (!canCapturePhotoOnDevice) {
            toast.info("Photo replacement capture is available on mobile devices only.", { autoClose: 1300 });
            return;
        }

        if (!photoId || isDamagePhotoMutating) return;

        setReplaceTargetPhotoId(Number(photoId));

        if (replaceDamagePhotoInputRef.current) {
            replaceDamagePhotoInputRef.current.value = "";
            replaceDamagePhotoInputRef.current.click();
        }
    }

    async function handleReplaceDamagePhotoSelection(event) {
        const selectedFile = event?.target?.files?.[0] || null;
        event.target.value = "";

        const targetPhotoId = replaceTargetPhotoId;
        setReplaceTargetPhotoId(null);

        if (!selectedFile || !targetPhotoId || !transactionDetail?.transactionId) return;

        const mimeType = String(selectedFile?.type || "").toLowerCase();
        if (mimeType && !mimeType.startsWith("image/")) {
            toast.error("Only image files are allowed.", { autoClose: 1200 });
            return;
        }

        if (selectedFile.size > MAX_RAW_DAMAGE_PHOTO_BYTES) {
            toast.error("Photo is too large before compression (8MB max).", { autoClose: 1200 });
            return;
        }

        try {
            setIsDamagePhotoMutating(true);
            setActiveDamagePhotoActionId(Number(targetPhotoId));
            setActiveDamagePhotoActionType("replace");

            const { file: compressedFile } = await compressImageFile(selectedFile, {
                maxDimension: 1280,
                quality: 0.72,
                outputMimeType: "image/jpeg",
                outputExtension: "jpg",
            });

            if (compressedFile.size > MAX_COMPRESSED_DAMAGE_PHOTO_BYTES) {
                toast.error("Compressed photo is still too large (2MB max).", { autoClose: 1200 });
                return;
            }

            const res = await ValetApi.replaceTransactionDamagePhoto(Number(transactionDetail.transactionId), Number(targetPhotoId), compressedFile);
            setDamageAssessment(res?.data?.damages || null);
            toast.success("Damage photo replaced.", { autoClose: 1000 });
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Unable to replace damage photo"), { autoClose: 1200 });
        } finally {
            setIsDamagePhotoMutating(false);
            setActiveDamagePhotoActionId(null);
            setActiveDamagePhotoActionType("");
        }
    }

    async function loadDamageReportPayload() {
        if (!transactionDetail?.transactionId) {
            throw new Error("No transaction selected");
        }

        const res = await ValetApi.getTransactionDamagesReport(Number(transactionDetail.transactionId));
        const report = res?.data?.report;

        if (!report) {
            throw new Error("No report payload available");
        }

        return report;
    }

    async function handleOpenDamageReport() {
        if (!transactionDetail?.transactionId || isDamageReportLoading) return;

        try {
            setShowDamages(false);
            setShowDamageReportModal(true);
            setDamageReport(null);
            setDamageReportError("");
            setIsDamageReportLoading(true);

            const report = await loadDamageReportPayload();
            setDamageReport(report);
        } catch (err) {
            setDamageReport(null);
            const message = getApiErrorMessage(err, "Unable to load damage report");
            setDamageReportError(message);
            toast.error(message, { autoClose: 1200 });
        } finally {
            setIsDamageReportLoading(false);
        }
    }

    function viewTransaction() {
        const statusId = Number(transactionDetail.transactionStatusId ?? transactionDetail.vehicleStatusId);

        // Button enable logic
        const canStage = statusId === 1;
        const canPark = statusId === 1 || statusId === 2 || statusId === 4 || statusId === 5;
        const canRequest = statusId === 1 || statusId === 2 || statusId === 3;
        const canRetrieve = statusId === 2 || statusId === 3 || statusId === 4;
        const canCheckout = statusId === 5;
        const canTransfer = statusId !== 6;

        const paymentModelLabel = {
            flat_rate: "Flat Rate",
            time_increment: "Time Increment",
            time_table: "Time Table",
        };

        const isPaymentFeatureEnabled = isPaymentsFeatureEnabled === true && paymentStatus?.featureEnabled === true;
        const requiresPayment = isPaymentFeatureEnabled && Boolean(paymentStatus?.requiresPayment);
        const shouldShowPaymentFlags = isPaymentFeatureEnabled && (Boolean(paymentStatus?.requiresPayment) || (paymentStatus?.paymentStatus === "paid" && Number(paymentStatus?.amountDueCents || 0) > 0));

        const isPaymentSettled = Boolean(paymentStatus?.isPaid);

        const garageDetailSettings = settings?.garageDetail || {};
        const visibleFields = garageDetailSettings?.visibleFields || {};
        const shouldShowPaymentControls = isPaymentFeatureEnabled && visibleFields.payment !== false;
        const showLabels = garageDetailSettings?.showFieldLabels !== false;

        const mobileValue = garageDetailSettings?.maskPhoneFirstSeven ? getMaskedPhone(transactionDetail.vehicleMobile) : formatPhone(transactionDetail.vehicleMobile);
        const damageMarks = Array.isArray(damageAssessment?.marks) && damageAssessment.marks.length > 0 ? damageAssessment.marks : safeJsonParse(transactionDetail.vehicleDamages || "[]");
        const damagePhotos = Array.isArray(damageAssessment?.photos) ? damageAssessment.photos : [];
        const canEditVehicleFields = visibleFields.make !== false || visibleFields.color !== false || visibleFields.note !== false;

        function renderFieldValue(label, value) {
            if (showLabels) {
                return (
                    <>
                        {label}: {value}
                    </>
                );
            }

            return value;
        }

        return (
            <div className="garage-detail-page">
                <div className="transaction-details">
                    <ListGroup flush>
                        <ListGroupItem>
                            <div className="podium-row">
                                <Button
                                    color="light"
                                    className="garage-lost-keys-btn"
                                    onClick={handleOpenLostKeys}>
                                    Lost Keys
                                </Button>
                            </div>
                            <div className="podium-row">{visibleFields.podium === false ? null : <span>{renderFieldValue("Podium", transactionDetail.podiumName)}</span>}</div>
                        </ListGroupItem>
                        {visibleFields.ticket === false ? null : <ListGroupItem>{renderFieldValue("Ticket", transactionDetail.vehicleTicketNum)}</ListGroupItem>}
                        {visibleFields.mobile === false ? null : <ListGroupItem>{renderFieldValue("Mobile", mobileValue)}</ListGroupItem>}
                        {visibleFields.make === false ? null : (
                            <ListGroupItem>
                                <div className={`vehicle-meta-row${isEditingVehicleMeta ? " is-editing" : ""}`}>
                                    {showLabels ? <span>Make: </span> : null}
                                    {isEditingVehicleMeta ? (
                                        <Input
                                            type="select"
                                            className="vehicle-meta-select"
                                            value={vehicleMetaDraft.make}
                                            onChange={(e) => {
                                                const nextMake = e.target.value;
                                                setVehicleMetaDraft((prev) => ({ ...prev, make: nextMake }));
                                            }}>
                                            <option value="">Select make</option>
                                            {VEHICLE_MAKE_OPTIONS.map((makeOption) => (
                                                <option
                                                    key={makeOption}
                                                    value={makeOption}>
                                                    {makeOption}
                                                </option>
                                            ))}
                                        </Input>
                                    ) : (
                                        <strong className="vehicle-meta-value">{transactionDetail.vehicleMake || "-"}</strong>
                                    )}
                                </div>
                            </ListGroupItem>
                        )}
                        {visibleFields.color === false ? null : (
                            <ListGroupItem>
                                <div className={`vehicle-meta-row${isEditingVehicleMeta ? " is-editing" : ""}`}>
                                    {showLabels ? <span>Color: </span> : null}
                                    {isEditingVehicleMeta ? (
                                        <Input
                                            type="select"
                                            className="vehicle-meta-select"
                                            value={vehicleMetaDraft.color}
                                            onChange={(e) => {
                                                const nextColor = e.target.value;
                                                setVehicleMetaDraft((prev) => ({ ...prev, color: nextColor }));
                                            }}>
                                            <option value="">Select color</option>
                                            {VEHICLE_COLOR_OPTIONS.map((colorOption) => (
                                                <option
                                                    key={colorOption}
                                                    value={colorOption.toLowerCase()}>
                                                    {colorOption}
                                                </option>
                                            ))}
                                        </Input>
                                    ) : (
                                        <strong className="vehicle-meta-value">{transactionDetail.vehicleColor || "-"}</strong>
                                    )}
                                </div>
                            </ListGroupItem>
                        )}
                        {visibleFields.parkingSpot === false ? null : (
                            <ListGroupItem>
                                <div className={`parking-spot-row${isEditingParkingSpot ? " is-editing" : ""}`}>
                                    {showLabels ? <span>Parking Spot: </span> : null}
                                    {isEditingParkingSpot ? (
                                        <>
                                            <Input
                                                className="parking-spot-input"
                                                value={parkingSpotInput}
                                                onChange={(e) => setParkingSpotInput(e.target.value)}
                                                placeholder="e.g. d4, row 1, 4-28"
                                                maxLength={64}
                                            />
                                            <Button
                                                color="primary"
                                                size="sm"
                                                className="parking-spot-save-btn"
                                                disabled={isSavingParkingSpot}
                                                onClick={handleSaveParkingSpot}>
                                                {isSavingParkingSpot ? "Saving..." : "Save"}
                                            </Button>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                className="parking-spot-cancel-btn"
                                                disabled={isSavingParkingSpot}
                                                onClick={() => {
                                                    setParkingSpotInput(transactionDetail.parkingSpot || "");
                                                    setIsEditingParkingSpot(false);
                                                }}>
                                                Cancel
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                className="parking-spot-value-btn"
                                                onClick={() => {
                                                    if (transactionDetail.parkingSpot) setShowParkingSpotZoom(true);
                                                }}>
                                                <strong className="parking-spot-value">{transactionDetail.parkingSpot || "Not set"}</strong>
                                            </button>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                className="parking-spot-action-btn"
                                                onClick={() => setIsEditingParkingSpot(true)}>
                                                {transactionDetail.parkingSpot ? "Edit Spot" : "Set Spot"}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </ListGroupItem>
                        )}
                        {visibleFields.note === false ? null : (
                            <ListGroupItem>
                                <div className={`vehicle-meta-row${isEditingVehicleMeta ? " is-editing" : ""}`}>
                                    {showLabels ? <span>Note: </span> : null}
                                    {isEditingVehicleMeta ? (
                                        <Input
                                            type="text"
                                            className="vehicle-meta-input"
                                            value={vehicleMetaDraft.notes}
                                            onChange={(e) => {
                                                const nextNotes = e.target.value;
                                                setVehicleMetaDraft((prev) => ({ ...prev, notes: nextNotes }));
                                            }}
                                            maxLength={255}
                                            placeholder="Add note"
                                        />
                                    ) : (
                                        <strong className="vehicle-meta-value">{transactionDetail.vehicleNotes || "-"}</strong>
                                    )}
                                </div>
                            </ListGroupItem>
                        )}
                        {/* <ListGroupItem>Transaction Status : {transactionDetail.statusDescription}</ListGroupItem> */}
                        {visibleFields.status === false ? null : <ListGroupItem>{renderFieldValue("Current Status", transactionDetail.transactionStatusDescription || transactionDetail.vehicleStatusDescription || transactionDetail.statusDescription)}</ListGroupItem>}
                        {shouldShowPaymentFlags && visibleFields.payment !== false ? (
                            <ListGroupItem>
                                <div className="payment-status-row">
                                    <div className="payment-status-copy">
                                        {showLabels ? <strong>Payment:</strong> : null} {isPaymentStatusLoading ? "Checking..." : paymentStatus?.paymentStatus === "paid" ? "Paid" : "Pending"}
                                        {paymentStatus?.amountDueCents > 0 ? ` ($${(paymentStatus.amountDueCents / 100).toFixed(2)})` : ""}
                                        {paymentStatus?.paymentModel ? ` • ${paymentModelLabel[paymentStatus.paymentModel] || paymentStatus.paymentModel}` : ""}
                                    </div>
                                </div>
                            </ListGroupItem>
                        ) : null}
                        {/* Damage Image, show button or show image with damage marks */}
                        {showDamages ? (
                            <ListGroupItem>
                                <div className="damage-image-modal">
                                    <div className="damage-image-header">
                                        <span>Damages</span>
                                        <div className="damage-image-header-actions">
                                            <Button
                                                type="button"
                                                color="secondary"
                                                size="sm"
                                                className="damage-report-btn"
                                                onClick={handleOpenDamageReport}
                                                disabled={isDamageReportLoading || isDamageAssessmentLoading}>
                                                {isDamageReportLoading ? "Preparing..." : "Review Report"}
                                            </Button>
                                            <Button
                                                color="link"
                                                size="sm"
                                                className="damage-image-close"
                                                onClick={() => setShowDamages(false)}
                                                aria-label="Close">
                                                Close
                                            </Button>
                                        </div>
                                    </div>
                                    {canCapturePhotoOnDevice ? (
                                        <input
                                            type="file"
                                            ref={replaceDamagePhotoInputRef}
                                            accept="image/*"
                                            capture="environment"
                                            className="damage-photo-hidden-input"
                                            onChange={handleReplaceDamagePhotoSelection}
                                        />
                                    ) : null}
                                    {isDamageAssessmentLoading ? <div className="damage-assessment-state">Loading damage evidence...</div> : null}
                                    {damageAssessmentError ? <div className="damage-assessment-state damage-assessment-state-error">{damageAssessmentError}</div> : null}
                                    <DamageImage
                                        damagePositions={damageMarks}
                                        className="damage-image-content"
                                    />
                                    {damagePhotos.length > 0 ? (
                                        <div className="damage-photo-gallery">
                                            {damagePhotos.map((photo) => {
                                                const isPhotoBusy = isDamagePhotoMutating && Number(activeDamagePhotoActionId) === Number(photo.id);

                                                return (
                                                    <div
                                                        key={photo.id}
                                                        className="damage-photo-card">
                                                        {photo.url ? (
                                                            <a
                                                                href={photo.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer">
                                                                <img
                                                                    src={photo.url}
                                                                    alt="Damage evidence"
                                                                    className="damage-photo-thumb"
                                                                />
                                                            </a>
                                                        ) : (
                                                            <div className="damage-photo-empty">Preview unavailable</div>
                                                        )}
                                                        <div className="damage-photo-meta">{photo.originalFileName || "Damage photo"}</div>
                                                        <div className="damage-photo-actions">
                                                            {canCapturePhotoOnDevice ? (
                                                                <Button
                                                                    type="button"
                                                                    color="secondary"
                                                                    size="sm"
                                                                    className="damage-photo-action-btn"
                                                                    disabled={isDamagePhotoMutating}
                                                                    onClick={() => handleStartReplaceDamagePhoto(photo.id)}>
                                                                    {isPhotoBusy && activeDamagePhotoActionType === "replace" ? "Replacing..." : "Replace"}
                                                                </Button>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="damage-photo-empty">No damage photos on this visit.</div>
                                    )}
                                </div>
                            </ListGroupItem>
                        ) : null}
                    </ListGroup>
                </div>
                <div className="garage-action-dock">
                    <div
                        className="garage-detail-tools"
                        role="group"
                        aria-label="Vehicle details actions">
                        <Button
                            color="danger"
                            size="sm"
                            onClick={() => setShowDamages(true)}>
                            Damages
                        </Button>
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => setShowHistory(true)}>
                            View History
                        </Button>
                        {canEditVehicleFields ? (
                            isEditingVehicleMeta ? (
                                <>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        disabled={isSavingVehicleMeta}
                                        onClick={handleSaveVehicleMeta}>
                                        {isSavingVehicleMeta ? "Saving..." : "Save"}
                                    </Button>
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        disabled={isSavingVehicleMeta}
                                        onClick={handleCancelEditVehicleMeta}>
                                        Cancel
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    color="primary"
                                    size="sm"
                                    onClick={handleStartEditVehicleMeta}>
                                    Edit Vehicle
                                </Button>
                            )
                        ) : null}
                    </div>
                    <div className="garage-detail-footer">
                        {/* Status badge */}
                        <div className="garage-status-badge">{transactionDetail.transactionStatusDescription || transactionDetail.vehicleStatusDescription || transactionDetail.statusDescription}</div>
                        {/* {Back to dashboard button} */}
                        <Button
                            className="garage-back-btn"
                            onClick={() => history.push("/valetDashBoard")}>
                            Dashboard
                        </Button>

                        {/* Only show the next valid action button */}
                        {canStage && (
                            <Button
                                color="primary"
                                className="garage-action-btn"
                                onClick={async () => {
                                    applyLocalStatus(2);
                                    try {
                                        await stageVehicle(userDetail.id, transactionDetail.vehicleId, transactionDetail.podiumId, transactionDetail.locationId);
                                    } finally {
                                        await fetchTransactions();
                                    }
                                }}>
                                Stage
                            </Button>
                        )}
                        {canPark && (
                            <Button
                                color="primary"
                                className="garage-action-btn"
                                onClick={async () => {
                                    const normalizedSpot = (parkingSpotInput || "").trim();
                                    applyLocalStatus(3);
                                    try {
                                        await parkVehicle(userDetail.id, transactionDetail.vehicleId, transactionDetail.podiumId, transactionDetail.locationId, normalizedSpot || null);
                                    } finally {
                                        await fetchTransactions();
                                    }
                                }}>
                                Park
                            </Button>
                        )}
                        {/* {canRequest && (
                        <Button
                            color="primary"
                            className="garage-action-btn"
                            onClick={async () => {
                                setTransactionDetail((prev) => ({ ...prev, vehicleStatusId: 4 }));
                                try {
                                    await requestVehicle(userDetail.id, transactionDetail.vehicleId, transactionDetail.podiumId, transactionDetail.locationId);
                                } finally {
                                    await fetchTransactions();
                                }
                            }}>
                            Request
                        </Button>
                    )} */}
                        {canRequest && (
                            <Button
                                color="primary"
                                className="garage-action-btn"
                                disabled={isActionBusy}
                                onClick={async () => {
                                    if (isActionBusy) return;

                                    setIsActionBusy(true);
                                    applyLocalStatus(4);

                                    try {
                                        await requestVehicle(userDetail.id, transactionDetail.vehicleId, transactionDetail.podiumId, transactionDetail.locationId);
                                    } finally {
                                        await fetchTransactions();
                                        setIsActionBusy(false);
                                    }
                                }}>
                                {isActionBusy ? "Requesting..." : "Request"}
                            </Button>
                        )}
                        {canRetrieve && (
                            <Button
                                color="primary"
                                className="garage-action-btn"
                                onClick={async () => {
                                    applyLocalStatus(5);
                                    try {
                                        await retrieveVehicle(userDetail.id, transactionDetail.vehicleId, transactionDetail.podiumId, transactionDetail.locationId);
                                    } finally {
                                        await fetchTransactions();
                                    }
                                }}>
                                Retrieve
                            </Button>
                        )}

                        {/* {canCheckout && (
                        <Button
                            color="primary"
                            className="garage-action-btn"
                            onClick={async () => {
                                setTransactionDetail((prev) => ({ ...prev, vehicleStatusId: 6 }));
                                try {
                                    await checkoutVehicle(userDetail.id, transactionDetail.vehicleId, transactionDetail.podiumId, transactionDetail.locationId);
                                } finally {
                                    await fetchTransactions();
                                }
                            }}>
                            Check Out
                        </Button>
                    )} */}

                        {canCheckout && (
                            <Button
                                color="primary"
                                className="garage-action-btn"
                                onClick={() => {
                                    resetVerifyState();
                                    setShowVerifyModal(true);
                                }}>
                                Check Out
                            </Button>
                        )}
                        {canTransfer && (
                            <Button
                                color="warning"
                                className="garage-action-btn"
                                onClick={() => {
                                    if (!activeTransferSourcePodiumId) {
                                        toast.info("Please select an active podium before starting a transfer.", { autoClose: 1200 });
                                        return;
                                    }
                                    setTransferDestinationPodiumId("");
                                    setTransferNote("");
                                    setShowTransferModal(true);
                                }}>
                                Transfer
                            </Button>
                        )}
                    </div>
                    {shouldShowPaymentControls ? (
                        <div className="garage-payment-footer">
                            <Button
                                color="secondary"
                                className="garage-payment-btn"
                                onClick={() => fetchPaymentStatus(transactionDetail.vehicleId)}>
                                Refresh Payment
                            </Button>
                            {requiresPayment && !isPaymentSettled ? (
                                <Button
                                    color="success"
                                    className="garage-payment-btn"
                                    onClick={() => setShowPaymentModal(true)}>
                                    Collect Payment
                                </Button>
                            ) : null}
                            <Button
                                color="info"
                                className="garage-payment-btn"
                                onClick={() =>
                                    history.push("/validations/redeem", {
                                        ticketNum: transactionDetail?.vehicleTicketNum || "",
                                        mobile: transactionDetail?.vehicleMobile || "",
                                    })
                                }>
                                Validate
                            </Button>
                        </div>
                    ) : null}
                </div>
                {/* START */}
                <Modal
                    isOpen={showVerifyModal}
                    toggle={() => {
                        setShowVerifyModal(false);
                        resetVerifyState();
                    }}
                    modalClassName="garage-modal-theme"
                    centered>
                    <ModalHeader
                        toggle={() => {
                            setShowVerifyModal(false);
                            resetVerifyState();
                        }}>
                        Verify Customer And Vehicle
                    </ModalHeader>
                    <ModalBody>
                        <div style={{ marginBottom: "10px" }}>
                            Expected{" "}
                            <ReceiptText
                                size={20}
                                aria-hidden="true"
                                style={{ verticalAlign: "middle" }}
                            />
                            : <strong>{transactionDetail.vehicleTicketNum}</strong>
                        </div>
                        <div style={{ marginBottom: "10px" }}>
                            Expected{" "}
                            <Phone
                                size={20}
                                aria-hidden="true"
                                style={{ verticalAlign: "middle" }}
                            />{" "}
                            Last 4: <strong>{getLast4(transactionDetail.vehicleMobile)}</strong>
                        </div>

                        <Button
                            color="secondary"
                            size="sm"
                            onClick={() => setShowScanner(true)}
                            aria-label="Scan Customer QR"
                            title="Scan Customer QR"
                            style={{ marginBottom: "10px" }}>
                            Scan
                            <ScanQrCode
                                size={36}
                                aria-hidden="true"
                                strokeWidth={1.5}
                            />
                        </Button>

                        <Input
                            type="tel"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder="Type ticket number or phone last 4"
                            value={typedVerificationInput}
                            onChange={(e) => {
                                setTypedVerificationInput(normalizeDigits(e.target.value));
                                setScannedTicket("");
                                setVerifyStatus("");
                                setVerifyMessage("");
                            }}
                        />

                        {scannedTicket ? (
                            <div className="verify-scanned-ticket">
                                Scanned Ticket: <strong>{scannedTicket}</strong>
                            </div>
                        ) : null}

                        {verifyMessage ? <div className={`verify-feedback ${verifyStatus === "ok" ? "verify-feedback-ok" : "verify-feedback-fail"}`}>{verifyMessage}</div> : null}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="info"
                            onClick={() => handleVerifyAndCheckout("phone")}
                            aria-label={isCheckingOut ? "Checking Out..." : "Verify Phone Last 4"}
                            title="Verify Phone Last 4"
                            disabled={isCheckingOut}>
                            {isCheckingOut ? (
                                "Checking Out..."
                            ) : (
                                <>
                                    Verify{" "}
                                    <Phone
                                        size={20}
                                        aria-hidden="true"
                                        style={{ verticalAlign: "middle" }}
                                    />
                                </>
                            )}
                        </Button>
                        <Button
                            color="secondary"
                            onClick={() => {
                                setShowVerifyModal(false);
                                resetVerifyState();
                            }}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => handleVerifyAndCheckout("ticket")}
                            aria-label={isCheckingOut ? "Checking Out..." : "Verify Ticket"}
                            title="Verify Ticket"
                            disabled={isCheckingOut}>
                            {isCheckingOut ? (
                                "Checking Out..."
                            ) : (
                                <>
                                    Verify{" "}
                                    <ReceiptText
                                        size={20}
                                        aria-hidden="true"
                                        style={{ verticalAlign: "middle" }}
                                    />
                                </>
                            )}
                        </Button>
                    </ModalFooter>
                </Modal>

                <LiveTicketScannerModal
                    isOpen={showScanner}
                    toggle={() => setShowScanner(false)}
                    onDetected={(ticket) => {
                        const normalized = normalizeDigits(ticket);
                        setScannedTicket(normalized);
                        setVerifyStatus("");
                        setVerifyMessage("");
                        setShowScanner(false);

                        if (normalized) {
                            handleVerifyAndCheckout("ticket", normalized);
                        }
                    }}
                />

                <Modal
                    isOpen={showParkingSpotZoom}
                    toggle={() => setShowParkingSpotZoom(false)}
                    modalClassName="garage-modal-theme"
                    centered>
                    <ModalHeader toggle={() => setShowParkingSpotZoom(false)}>Parking Spot</ModalHeader>
                    <ModalBody>
                        <div className="parking-spot-zoom-value">{transactionDetail?.parkingSpot || "Not set"}</div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="primary"
                            onClick={() => setShowParkingSpotZoom(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>

                <Modal
                    isOpen={showTransferModal}
                    toggle={() => setShowTransferModal(false)}
                    modalClassName="garage-modal-theme"
                    centered>
                    <ModalHeader toggle={() => setShowTransferModal(false)}>Initiate Transfer</ModalHeader>
                    <ModalBody>
                        <div style={{ marginBottom: "8px" }}>
                            Vehicle Ticket: <strong>{transactionDetail.vehicleTicketNum}</strong>
                        </div>
                        <div style={{ marginBottom: "8px" }}>
                            Current Podium: <strong>{activeTransferSourcePodiumName}</strong>
                        </div>
                        <div style={{ marginBottom: "8px" }}>
                            Destination Podium
                            <Input
                                type="select"
                                value={transferDestinationPodiumId}
                                onChange={(e) => setTransferDestinationPodiumId(e.target.value)}>
                                <option value="">Select destination podium</option>
                                {destinationPodiums.map((p) => (
                                    <option
                                        key={p.id}
                                        value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </Input>
                        </div>
                        <div>
                            Note (optional)
                            <Input
                                type="text"
                                value={transferNote}
                                onChange={(e) => setTransferNote(e.target.value)}
                                placeholder="Transfer handoff notes"
                            />
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="secondary"
                            onClick={() => setShowTransferModal(false)}>
                            Close
                        </Button>
                        <Button
                            color="warning"
                            onClick={handleInitiateTransfer}
                            disabled={!transferDestinationPodiumId || isTransferBusy}>
                            {isTransferBusy ? "Initiating..." : "Initiate Transfer"}
                        </Button>
                    </ModalFooter>
                </Modal>

                <Modal
                    isOpen={showLostKeysModal}
                    toggle={() => setShowLostKeysModal(false)}
                    modalClassName="garage-modal-theme"
                    centered>
                    <ModalHeader toggle={() => setShowLostKeysModal(false)}>
                        Lost Keys - Next Activity
                        {lostKeysData.anchorUsername ? ` (${lostKeysData.anchorUsername})` : ""}
                    </ModalHeader>
                    <ModalBody>
                        <div className="lost-keys-note">Next 3 parked and next 3 retrieved by the valet who parked this vehicle.</div>

                        {isLostKeysLoading ? (
                            <div className="lost-keys-loading">Loading...</div>
                        ) : (
                            <div className="lost-keys-grid">
                                <div>
                                    <div className="lost-keys-section-title">Parked</div>
                                    {lostKeysData.parked.length ? (
                                        <ListGroup>
                                            {lostKeysData.parked.map((row) => (
                                                <ListGroupItem key={`parked-${row.transactionId}`}>
                                                    <strong className="lost-keys-ticket">Ticket {row.vehicleTicketNum}</strong>
                                                    <div className="lost-keys-meta">
                                                        {row.vehicleMake || "Unknown"} {row.vehicleColor || ""}
                                                    </div>
                                                    <div className="lost-keys-meta">{formatHistoryTimestampShort(row.transactionTime)}</div>
                                                </ListGroupItem>
                                            ))}
                                        </ListGroup>
                                    ) : (
                                        <div className="lost-keys-empty">No parked vehicles found.</div>
                                    )}
                                </div>

                                <div>
                                    <div className="lost-keys-section-title">Retrieved</div>
                                    {lostKeysData.retrieved.length ? (
                                        <ListGroup>
                                            {lostKeysData.retrieved.map((row) => (
                                                <ListGroupItem key={`retrieved-${row.transactionId}`}>
                                                    <strong className="lost-keys-ticket">Ticket {row.vehicleTicketNum}</strong>
                                                    <div className="lost-keys-meta">
                                                        {row.vehicleMake || "Unknown"} {row.vehicleColor || ""}
                                                    </div>
                                                    <div className="lost-keys-phone">
                                                        Phone: <strong>{row.vehicleMobile ? formatPhone(row.vehicleMobile) : "Unavailable"}</strong>
                                                    </div>
                                                    <div className="lost-keys-meta">{formatHistoryTimestampShort(row.transactionTime)}</div>
                                                </ListGroupItem>
                                            ))}
                                        </ListGroup>
                                    ) : (
                                        <div className="lost-keys-empty">No retrieved vehicles found.</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="secondary"
                            onClick={() => setShowLostKeysModal(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>

                <Modal
                    isOpen={showDamageReportModal}
                    toggle={() => setShowDamageReportModal(false)}
                    modalClassName="garage-modal-theme damage-report-modal-theme"
                    centered
                    size="lg">
                    <ModalHeader toggle={() => setShowDamageReportModal(false)}>Damage Report Review</ModalHeader>
                    <ModalBody>
                        <DamageReportReview
                            report={damageReport}
                            isLoading={isDamageReportLoading}
                            error={damageReportError}
                            emptyMessage="No damage report available for this visit."
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="secondary"
                            onClick={() => setShowDamageReportModal(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>
                {/* END */}
                <PaymentCollectionModal
                    isOpen={showPaymentModal}
                    toggle={() => setShowPaymentModal(false)}
                    vehicleId={transactionDetail?.vehicleId}
                    podiumId={transactionDetail?.podiumId || activePodiumId}
                    allowManualFallback={true}
                    onPaymentUpdated={async () => {
                        await fetchPaymentStatus(transactionDetail?.vehicleId);
                        await fetchTransactions();
                    }}
                />
                {showHistory && (
                    <div className="transaction-history-modal">
                        <div className="transaction-history-header">
                            <span>Transaction History</span>
                            <Button
                                color="link"
                                size="sm"
                                className="transaction-history-close"
                                onClick={() => setShowHistory(false)}
                                aria-label="Close">
                                Close
                            </Button>
                        </div>

                        {transactionHistory === null ? (
                            <div style={{ padding: "16px", textAlign: "center", color: "#888" }}>Loading...</div>
                        ) : transactionHistory.length > 0 ? (
                            <div className="transaction-history-table">
                                <table className="transaction-history">
                                    <thead>
                                        <tr>
                                            <th>When</th>
                                            <th>Podium</th>
                                            <th>Spot</th>
                                            <th>Status</th>
                                            <th>User</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactionHistory.map((row, idx) => (
                                            <tr key={idx}>
                                                <td>{formatHistoryTimestampShort(row.transactionTime)}</td>
                                                <td>{row.podiumName}</td>
                                                <td>{row.parkingSpot || ""}</td>
                                                <td>{row.statusDescription}</td>
                                                <td title={row.username || ""}>{formatHistoryUser(row)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ padding: "16px", textAlign: "center", color: "#888" }}>No transaction history found.</div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return transactionDetail ? viewTransaction(transactionDetail) : <Spinner color="primary" />;
};

export default GarageDetail;
